import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import SelectedColor from '../Wizard/commonComps/ColorPicker/SelectedColor';
import { getColorStyle } from '../../actions/wizard';
import ColorSearch from "./../Wizard/commonComps/SearchBoxOptions";
import { Circle, CircleData, Step, StepOption } from '../../types/wizard';
import { addTarget } from '../../stores/wizard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const Tooltip = ({ text }: { text: string }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="relative flex items-center">
      <div
        className="cursor-pointer"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        <FontAwesomeIcon icon={faInfoCircle} className="hover:text-gray-700" />
      </div>
      {show && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 text-sm text-gray bg-white rounded shadow-lg whitespace-nowrap">
          {text}
        </div>
      )}
    </div>
  );
};

type ColorSelectorProps = {
  step: Step;
  singleSelection?: boolean;
  isClosestColor?: boolean;
  colorOption: StepOption | null;
  onCancelClick?: (isClosestColor?: boolean, index?: number) => void;
  onSelect: (colorData: CircleData) => void;
  index?: number;
  canAddNewColors: boolean;
  colorInstruction?: (comment: string) => void;
  initialInstructions?: string;
};

export const ColorSelector: React.FC<ColorSelectorProps> = (
  {
    step,
    singleSelection,
    isClosestColor,
    colorOption,
    index,
    onCancelClick,
    onSelect,
    canAddNewColors,
    colorInstruction,
    initialInstructions
  }) => {
  const [colors, setColors] = useState<StepOption[]>([]);
  const [selectedColor, setSelectedColor] = useState<StepOption | null>(colorOption);
  const [instructions, setInstructions] = useState(initialInstructions);
  const showClosest = isClosestColor && selectedColor && colorOption?.htmlColor === selectedColor?.htmlColor;
  const title = singleSelection && `EDIT ${step.title}`;

  useEffect(() => {
    if (colorOption)
      setSelectedColor(colorOption);
  }, [colorOption]);

  useEffect(() => {
    setInstructions(initialInstructions);
  }, [initialInstructions]);

  const handleColorSelect = (colorOption: StepOption) => {
    setSelectedColor(colorOption);

    if (singleSelection) {
      const colorData: CircleData = {
        option: colorOption,
        color: colorOption.htmlColor ?? "",
        stepId: step.id,
      };
      onSelect(colorData);
    }
  };

  const handleSearch = useCallback((filterResult: StepOption[]) => setColors(filterResult), []);

  const handleSave = () => {
    if (selectedColor) {
      const colorData: CircleData = {
        option: selectedColor,
        color: selectedColor.htmlColor ?? "",
        stepId: step.id,
      };
      onSelect(colorData);
    }
  };

  const handleCancel = () => {
    if (isClosestColor) {
      addTarget(step.id, {} as Circle);
      onCancelClick && onCancelClick(isClosestColor, index ?? 0);
    } else {
      onCancelClick && onCancelClick(false, index ?? 0);
    }
  };

  const handleSpecialInstructions = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newInstructions = event.target.value;
    setInstructions(newInstructions);
    if (colorInstruction) {
      colorInstruction(newInstructions);
    }
  };

  return (
    <div className='w-full'>
      <div className="flex items-center justify-center">
        <div className="w-full">
          {showClosest &&
            <div className='mb-2'>
              <span className="font-bold text-sm text-left uppercase">Automatically Selected Closest Match</span>
              <SelectedColor color={selectedColor} isClosestMatch={true} />
            </div>
          }
          <div className='flex flex-col items-start'>
            <div className='flex flex-row justify-start gap-2 mb-2'>
              <span className="font-bold text-lg uppercase">{title ?? "Select Colour"}</span>
              {!title && <Tooltip text="Choose a colour from the color picker list" />}
            </div>
            <span className="font-bold text-sm text-left mr-2 uppercase">Or Search By Code</span>
          </div>
          <ColorSearch onChange={handleSearch} placeHolder="Name or #" step={step} />
          <div className="gap-2 border h-24 overflow-hidden overflow-y-auto border-dashed border-x-0 py-2">
            <div className="flex w-full flex-wrap">
              {colors?.map((option: StepOption) => (
                <div
                  key={option.id}
                  onClick={(event) => { event.stopPropagation(); handleColorSelect(option) }}
                  className="w-6 h-6 cursor-pointer mr-1 mb-1 border border-slate-500/7"
                  style={{
                    ...getColorStyle(option),
                    transform: selectedColor?.id === option.id ? 'scale(1.2)' : 'none'
                  }}
                ></div>
              ))}
            </div>
          </div>
          {selectedColor && !showClosest && <SelectedColor color={selectedColor} />}
        </div>
      </div>
      {!singleSelection && selectedColor &&
        <React.Fragment>
          <div className='w-full flex flex-col items-start gap-2 my-2'>
            <h2 className="">
              <span className="font-bold uppercase mr-2">Special Instructions</span>
              <span>(Optional)</span>
            </h2>
            <textarea
              className='border w-full rounded h-auto p-1 outline-none'
              style={{ border: "1px solid #ccc" }}
              onChange={handleSpecialInstructions}
              placeholder={step?.specialInstructionsPlaceholder
                || 'Please provide additional, detailed instructions here to help with the production of your design.'}
              value={instructions}
            />
          </div>
          {canAddNewColors &&
            <div className="flex flex-row justify-between py-2">
              <button className="outline-none hover:outline-none focus:outline-none border-0 underline bg-transparent text-black" onClick={handleCancel}>Cancel</button>
              <button disabled={!selectedColor} onClick={handleSave} className="btn-file bg-black text-white underline rounded">Save Colour</button>
            </div>
          }
        </React.Fragment>
      }
    </div>
  );
};

export default ColorSelector;
